/**
 * Shared utilities — used by both frontend and backend
 * Formatting, validation, and helper functions
 */

// ─── Formatting ────────────────────────────────────────
export function formatCurrency(amount: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
}

/**
 * Live phone input formatter — call in onChange handlers.
 * Strips non-digits and applies (XXX) XXX-XXXX mask progressively as the user types.
 * Store the formatted string in state; isValidPhone() handles it on validation.
 */
export function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * The platform's default timezone.
 *
 * ⚠️ NOT "the timezone". Every date helper below now takes an optional IANA zone,
 * because `company_timezone` has been settable in the admin UI since the settings
 * page shipped and read by NOTHING — every tenant's dates were Eastern whether
 * they were in Florida or Oregon. This constant is the fallback so that every
 * existing call site keeps behaving exactly as it did.
 *
 * ⚠️ A STORED DATE DOES NOT MOVE WHEN THE SETTING CHANGES. `scheduled_date` holds
 * midnight in the zone that was in force when the row was written. Re-interpreting
 * old rows under a new zone would silently shift historical jobs by a day, so the
 * setting governs new writes and display only — and the settings UI has to say so.
 */
const TZ = 'America/New_York';

/**
 * Coerce anything into a valid Date, or null.
 *
 * Why this exists: `Intl.DateTimeFormat().format(new Date(undefined))` throws
 * `RangeError: Invalid time value`. Inside a React render that is not a bad
 * cell — it unmounts the entire page into the error boundary. A single row
 * with a missing timestamp took down all of /admin/referrals this way.
 * Formatters below return a dash instead of throwing.
 */
function toValidDate(date: Date | string | number | null | undefined): Date | null {
  if (date === null || date === undefined || date === '') return null;
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Rendered in place of a date that is missing or unparseable. */
const NO_DATE = '—';

/** "Jan 1, 2026" — SSR-safe, always Eastern Time */
export function formatDate(
  date: Date | string | number | null | undefined,
  locale = 'en-US',
  tz: string = TZ,
): string {
  const d = toValidDate(date);
  if (!d) return NO_DATE;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  }).format(d);
}

/** "Jan 1" — month + day only, no year */
export function formatDateShort(
  date: Date | string | number | null | undefined,
  locale = 'en-US',
  tz: string = TZ,
): string {
  const d = toValidDate(date);
  if (!d) return NO_DATE;
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  }).format(d);
}

/** "Jan 1, 2026, 12:30 PM" — SSR-safe, always Eastern Time */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  locale = 'en-US',
  tz: string = TZ,
): string {
  const d = toValidDate(date);
  if (!d) return NO_DATE;
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  }).format(d);
}

/** "12:30 PM" — time only, Eastern Time */
export function formatTime(
  date: Date | string | number | null | undefined,
  locale = 'en-US',
  tz: string = TZ,
): string {
  const d = toValidDate(date);
  if (!d) return NO_DATE;
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
  }).format(d);
}

/** "January 1, 2026" — long month name, SSR-safe */
export function formatDateLong(date: Date | string | number, locale = 'en-US', tz: string = TZ): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  }).format(d);
}

/** "Monday, January 1, 2026" — full weekday + long month */
export function formatDateFullWeekday(date: Date | string | number, locale = 'en-US', tz: string = TZ): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  }).format(d);
}

/** "Mon, Jan 1, 2026" — with short weekday */
export function formatDateWithWeekday(date: Date | string | number, locale = 'en-US', tz: string = TZ): string {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  }).format(d);
}

// ─── Eastern-Time Date Utilities ──────────────────────────
// NLC operates in St. Petersburg, FL. All user-facing dates
// are Eastern Time. These helpers ensure dates are stored and
// queried in Eastern, not UTC, so "April 18" always means
// April 18 in Florida — regardless of Cloudflare Worker TZ.

/**
 * Convert "YYYY-MM-DD" to Unix ms at midnight Eastern Time.
 * Handles EST/EDT automatically via Intl.
 *
 * Use this whenever storing a date from a form input or API
 * payload into `scheduled_date` or similar integer columns.
 */
export function easternMidnightMs(dateStr: string, tz: string = TZ): number {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number];
  // Use noon UTC as a probe — safely within the same calendar day in both UTC
  // and Eastern, so DST detection is reliable.
  const noonUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(noonUtc);
  /*
   * MINUTES, not just hours.
   *
   * This read only the hour and computed `12 - hour`, which is exact for every
   * US zone and wrong for every half-hour one: India (UTC+5:30), Newfoundland
   * (-3:30), South Australia (+9:30), Nepal (+5:45). While the platform was
   * hardcoded to America/New_York that could never fire; the moment a tenant can
   * choose their zone it silently puts midnight 30 minutes off, which lands a
   * job on the wrong calendar day at the boundary.
   *
   * `hour12: false` can render midnight as "24" in some locales, so normalise it.
   */
  const rawHour = parseInt(parts.find((p) => p.type === 'hour')!.value, 10);
  const localHour = rawHour === 24 ? 0 : rawHour;
  const localMinute = parseInt(parts.find((p) => p.type === 'minute')!.value, 10);
  // noon UTC reads as localHour:localMinute there → offset = 720 - those minutes
  const offsetMinutes = 12 * 60 - (localHour * 60 + localMinute);
  return Date.UTC(y, m - 1, d, 0, offsetMinutes, 0);
}

/**
 * Returns Unix ms at midnight Eastern for "today" (the current
 * calendar date in America/New_York). Use for day-boundary queries.
 */
export function startOfTodayEasternMs(tz: string = TZ): number {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const y = parseInt(parts.find((p) => p.type === 'year')!.value, 10);
  const m = parseInt(parts.find((p) => p.type === 'month')!.value, 10);
  const d = parseInt(parts.find((p) => p.type === 'day')!.value, 10);
  return easternMidnightMs(`${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`, tz);
}

/** One full day in milliseconds. */
export const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Returns the 7-day pay-period boundaries (Eastern midnight) that contain
 * the given Unix ms timestamp, with the period starting on `startDow`
 * (0=Sun..6=Sat). End is exclusive.
 *
 * NLC default is Wed→Tue (startDow=3) with payday on Friday — set via
 * `pay_period_start_day` in admin_settings. Callers fetch the setting
 * and pass it in so this helper stays pure/sync.
 */
export function getPayPeriodBounds(
  dateMs: number,
  startDow: number,
  tz: string = TZ,
): { weekStart: number; weekEnd: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
  }).formatToParts(new Date(dateMs));
  const weekdayStr = parts.find((p) => p.type === 'weekday')!.value;
  const DOW: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = DOW[weekdayStr] ?? 0;
  const daysSinceStart = (dow - startDow + 7) % 7;
  const dateMidnight = easternMidnightMs(toEasternDateStr(dateMs, tz), tz);
  const weekStart = dateMidnight - daysSinceStart * MS_PER_DAY;
  return { weekStart, weekEnd: weekStart + 7 * MS_PER_DAY };
}

/**
 * @deprecated Use `getPayPeriodBounds(dateMs, startDow)` with a configurable
 * start-of-week. Retained as a delegation shim so existing call sites keep
 * compiling. New code should read `pay_period_start_day` from settings.
 */
export function getFriThurWeekBounds(dateMs: number): { weekStart: number; weekEnd: number } {
  return getPayPeriodBounds(dateMs, 5);
}

/**
 * Convert a Unix ms timestamp to "YYYY-MM-DD" in Eastern Time.
 * Use for populating HTML date inputs from stored timestamps.
 */
export function toEasternDateStr(ms: number, tz: string = TZ): string {
  const d = new Date(ms);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year')!.value;
  const m = parts.find((p) => p.type === 'month')!.value;
  const day = parts.find((p) => p.type === 'day')!.value;
  return `${y}-${m}-${day}`;
}

/**
 * Convert a Unix ms timestamp to "HH:MM" (24h) in Eastern Time.
 * Use for populating HTML time inputs from stored timestamps.
 */
export function toEasternTimeStr(ms: number, tz: string = TZ): string {
  const d = new Date(ms);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const h = parts.find((p) => p.type === 'hour')!.value;
  const min = parts.find((p) => p.type === 'minute')!.value;
  return `${h}:${min}`;
}

/**
 * Get Eastern-time year/month/day components from a Unix ms timestamp.
 */
export function easternDateParts(ms: number, tz: string = TZ): { year: number; month: number; day: number } {
  const d = new Date(ms);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  return {
    year: parseInt(parts.find((p) => p.type === 'year')!.value, 10),
    month: parseInt(parts.find((p) => p.type === 'month')!.value, 10),
    day: parseInt(parts.find((p) => p.type === 'day')!.value, 10),
  };
}

// ─── Validation ────────────────────────────────────────
export function isValidPhone(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  // A usable US number is 10 digits, or 11 with a leading country-code 1.
  return cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'));
}


/**
 * Monday 00:00 Eastern for the week containing `ms`.
 *
 * ONE function, used everywhere a week is needed. The partner platform learned
 * this the hard way: Eastern is a MOVING UTC offset (-05:00 in winter, -04:00 in
 * summer), so a week computed in UTC puts a Sunday-evening entry in the wrong
 * week. With a `UNIQUE(person, week_start)` on the table, that does not surface
 * as an error — the second write collides with a row from the adjacent week and
 * looks like a phantom overwrite of something the author did not touch.
 *
 * Built on easternDateParts + easternMidnightMs so it inherits the same
 * offset handling as every other date in the system, rather than adding a second
 * opinion about what Eastern means.
 */
export function easternWeekStartMs(ms: number, tz: string = TZ): number {
  const { year, month, day } = easternDateParts(ms, tz);
  // getUTCDay on a UTC-constructed date gives the weekday of those calendar
  // parts, which is what we want — the parts are already Eastern.
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay(); // 0 = Sunday
  // Monday-based: Sunday belongs to the week that STARTED six days earlier.
  const daysSinceMonday = (weekday + 6) % 7;
  const monday = new Date(Date.UTC(year, month - 1, day - daysSinceMonday));
  const pad = (n: number) => String(n).padStart(2, '0');
  return easternMidnightMs(
    `${monday.getUTCFullYear()}-${pad(monday.getUTCMonth() + 1)}-${pad(monday.getUTCDate())}`,
    tz
  );
}

/** The Monday-based week containing `ms`, as an inclusive Eastern-midnight pair. */
export function easternWeekBounds(ms: number, tz: string = TZ): { weekStart: number; weekEnd: number } {
  const weekStart = easternWeekStartMs(ms, tz);
  const { year, month, day } = easternDateParts(weekStart, tz);
  const sunday = new Date(Date.UTC(year, month - 1, day + 6));
  const pad = (n: number) => String(n).padStart(2, '0');
  const weekEnd = easternMidnightMs(
    `${sunday.getUTCFullYear()}-${pad(sunday.getUTCMonth() + 1)}-${pad(sunday.getUTCDate())}`,
    tz
  );
  return { weekStart, weekEnd };
}
